import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { APIService } from 'app/services/api.service';
import { firstValueFrom } from 'rxjs';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { PipeConfiguration } from 'app/models/pipe.model';
import { Organization } from 'app/models/organization.model';
import {
  DELIVERYHERO_ID,
  GECOPOSSALES_ID,
  IExternalSystem, IIKO_ID, KITOPI_ID,
  KITOPIV3_ID
} from 'app/models/externalSystem.model';
import { MenuStorage } from 'app/models/menuStorage.model';
import { utcToLocalFormat } from 'app/shared/pipes/utc-to-local.pipe';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { RoleMembership } from "app/reducers/context.selectors";
import { Store } from '@ngxs/store';
import {
  ActivatorComponent,
  IActivatorInput,
  IActivatorOutput
} from 'app/shared/components/activator/activator.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';

@Component({
  selector: 'app-menu-pipes[menuPipes]',
  templateUrl: './menu-pipes-modal.component.html',
  styleUrls: ['./menu-pipes-modal.component.scss']
})
export class MenuPipesModalComponent extends BaseModalComponent implements OnInit, OnChanges {
  roleMembership: RoleMembership;
  menuPipes: PipeConfiguration[];
  // id селектора, когда нужно отобразить пайпы в таблице из gecoPos в агрегаторы.
  readonly AllAggregatorsId = 'AllAggregatorsId';
  loadingMenu: boolean = false;
  externalSystems: IExternalSystem[];
  branchOrganizations: Organization[];
  selectedOrganizationId: string; // организация в лк хаба, выбранная в главном меню
  selectedTableOrganizationId: string; // организация, выбранная в таблице пайпов
  selectedPipeType: string; // выбранный тип пайпов в таблице
  dateFormat: string = utcToLocalFormat;
  dataSource: MatTableDataSource<TablePipeDataItem> =
    new MatTableDataSource<TablePipeDataItem>([]);
  columnsToDisplay =
    ['checkBox', 'organization', 'pipe', 'uploadedAt', 'published', 'id'];
  // исходные данные для dataSource, особенно с динамическими значениями checked,
  // которые также имеют связи через [NgModel] в чекбоксах
  tablePipeData: TablePipeDataItem[];
  tableDistinctPipeTypes: string[];
  tableDistinctOrganizations: Organization[];

  constructor(
    public dialogRef: MatDialogRef<MenuPipesModalComponent>,
    private api: APIService,
    private translate: TranslateService,
    private store: Store,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: MenuPipesModalComponentDataType,
    )
  {
    super(dialogRef);
    this.menuPipes = this.data.menuPipes;
    this.roleMembership = this.data.roleMembership;
  }

  async ngOnInit() {
    this.externalSystems = this.store.selectSnapshot<IExternalSystem[]>(OrganizationsState.getExternalSystems);
    this.selectedOrganizationId = await firstValueFrom(this.store.select(OrganizationsState.getSelectedOrganizationId));
    this.branchOrganizations = await firstValueFrom(this.store.select(OrganizationsState.getSelectedOrganizationBranches));

    await this.buildDataForChangeMenuSection(this.branchOrganizations);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['menuPipes'].isFirstChange()){
      return;
    }
    await this.buildDataForChangeMenuSection(
      this.branchOrganizations);
  }

  async buildDataForChangeMenuSection(
    organizations: Organization[])
  {
    const pipeIds = this.menuPipes?.map(p => p.id) ?? [];
    const menuStorages = (await this.api.getLatestMenuStoragesByPipeIds(pipeIds)).result;

    this.tableDistinctPipeTypes  = this.uniqByFilter(
      this.menuPipes
        .map(p => this.externalSystems.find(s => s.key === p.sourceConfiguration.externalSystemType).externalSystemType)
    );

    this.tableDistinctOrganizations = this.getDistinctOrganizations(this.menuPipes, organizations);

    this.tablePipeData = this.buildTablePipeData(
      this.menuPipes,
      this.tableDistinctOrganizations,
      menuStorages);

    this.dataSource = new MatTableDataSource(this.tablePipeData);
    this.dataSource.filterPredicate = (item, filter) => {
      return this.isActiveTablePipeDataItem(item);
    };
  }

  getColumnHeaderName(columnHeaderValue: string): string {
    switch (columnHeaderValue) {
      case 'pipe':
        return this.translate.instant('LOCATION.PIPE');
      case 'uploadedAt':
        return this.translate.instant('MENU_HISTORY.UPLOADED_AT');
      case 'published':
        return this.translate.instant('MENU_HISTORY.PUBLISHED_AT');
      default:
        return '';
    }
  }

  async uploadMenuAsync(){
    this.loadingMenu = true;
    for (let pipeDataItem of this.tablePipeData
      .filter(item => this.isActiveTablePipeDataItem(item) && item.checked))
    {
      pipeDataItem.startedLoading = true;
      pipeDataItem.loading = true;
      try {
        const response = await firstValueFrom(this.api.uploadMenu(pipeDataItem.pipe.id));
      }
      finally {
        pipeDataItem.loading = false;
      }
    }

    await this.buildDataForChangeMenuSection(
      this.branchOrganizations);

    this.loadingMenu = false;
  }

  uniqByFilter<T>(array: T[]) {
    return array.filter((value, index) => array.indexOf(value) === index);
  }

  getDistinctOrganizations(
    filteredPipes: PipeConfiguration[],
    organizations: Organization[]){
    const distinctOrganizationIds = this.uniqByFilter(filteredPipes.map(p => p.organizationId));
    return organizations.filter(o => distinctOrganizationIds.some(id => id == o.id));
  }

  buildTablePipeData(
    filteredPipes: PipeConfiguration[],
    organizations: Organization[],
    menuStorages: MenuStorage[])
  {

    let result: TablePipeDataItem[] = [];

    if (!filteredPipes.length){
      return [];
    }

    for (let pipe of
      filteredPipes
        .sort((prev, cur) =>
          (prev.organizationId > cur.organizationId ? 1 : -1)))
    {
      result.push({
        pipe: pipe,
        organization: organizations.find(o => o.id == pipe.organizationId),
        checked: false,
        menuStorage: menuStorages.find(m => m.pipeIds.some(id => id === pipe.id)), // Следует подумать над тем, насколько оправданно получение menuStorages таким образом для разных пайпов, чтобы потом такую строчку использовать.
        sourceSystem: this.externalSystems.find(s => s.key === pipe.sourceConfiguration.externalSystemType),
        loading: false,
        startedLoading: false,
      });
    }

    return result;
  }

  isActiveTablePipeDataItem(item: TablePipeDataItem){
    return (item.organization.id === this.selectedTableOrganizationId || !this.selectedTableOrganizationId)
      && (item.sourceSystem.externalSystemType === this.selectedPipeType || !this.selectedPipeType
        || item.sourceSystem.key !== GECOPOSSALES_ID && this.selectedPipeType === this.AllAggregatorsId);
  }

  selectionChange() {
    this.dataSource.filter = Date.now().toString();
  }

  changeCheckedAllPipes(checked: boolean) {
    this.tablePipeData.forEach(item => {
      if(checked){
        if (this.isActiveTablePipeDataItem(item)
          && this.allowMultiplyUpload(item)){
          item.checked = true;
        }
        else{
          item.checked = false;
        }
      }
      else{
        item.checked = false;
      }
    });
  }

  allowMultiplyUpload(item: TablePipeDataItem): boolean{
    const sourceExternalSystemType = item.pipe.sourceConfiguration.externalSystemType;
    const targetExternalSystemType = item.pipe.targetConfiguration.externalSystemType;

    // TODO: лучше создать параметр для агрегатора на сервере хаба вместо это проверки
    if(sourceExternalSystemType === GECOPOSSALES_ID
      || targetExternalSystemType === DELIVERYHERO_ID
      || targetExternalSystemType === KITOPI_ID
      || targetExternalSystemType === KITOPIV3_ID){
      return this.selectedOrganizationId === item.pipe.organizationId;
    }

    return true;
  }

  async confirmPublish(menuStorageId: string, pipeId: string) {
    if(!confirm(this.translate.instant('MENU_HISTORY.CONFIRM_PUBLISH'))){
      return;
    }
    const response = await this.api.confirmPublishingMenu(menuStorageId, pipeId);

    if(!response.error){
      await this.buildDataForChangeMenuSection(
        this.branchOrganizations);

      this.selectionChange();
    }
  }

  async cancelPublish(menuStorageId: string, pipeId: string) {
    if(!confirm( this.translate.instant('MENU_HISTORY.CONFIRM_CANCEL_PUBLISH'))){
      return;
    }
    const response = await this.api.cancelPublishingMenu(menuStorageId, pipeId);

    if(!response.error){
      await this.buildDataForChangeMenuSection(
        this.branchOrganizations);

      this.selectionChange();
    }
  }

  async setAllIikoTestModeAsync(){
    const ids = this.tablePipeData.filter(pd =>
      pd.pipe.sourceConfiguration.externalSystemType === GECOPOSSALES_ID &&
      pd.pipe.targetConfiguration.externalSystemType === IIKO_ID)
      .map(pd => pd.pipe.targetConfiguration.id);

    const activatorOutput = await firstValueFrom(this.dialog.open<ActivatorComponent, IActivatorInput, IActivatorOutput>(
      ActivatorComponent,
      {
        data: {
          message: this.translate.instant('MENU.SET_ALL_IIKO_TEST_MODE'),
        } as IActivatorInput
      }).afterClosed());

    if (!activatorOutput.isCanceled){
      await this.api.setTestModeAsync(ids, activatorOutput.value);
      // в случае успеха можно обновить пайпы.
    }
  }
}

interface TablePipeDataItem{
  pipe: PipeConfiguration,
  checked: boolean,
  organization: Organization,
  menuStorage: MenuStorage,
  sourceSystem: IExternalSystem,
  loading: boolean,
  startedLoading: boolean,
}

export interface MenuPipesModalComponentDataType {
  roleMembership: RoleMembership;
  menuPipes: PipeConfiguration[];
}
